<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import TapDrillTable from '#lib/tapDrill/TapDrillTable.svelte';
  import { METRIC_TAP_ROWS, STANDARD_TAP_ROWS } from '#lib/tapDrill/data.ts';
  import { onDestroy } from 'svelte';
  import { trackToolUse } from '#lib/analytics.ts';

  const SEARCH_TRACK_DEBOUNCE_MS = 1500;

  type Tab = 'standard' | 'metric';

  let activeTab: Tab = 'standard';
  let filter = '';

  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  function setTab(tab: Tab) {
    if (tab !== activeTab) trackToolUse('tap_drill', 'switch_tab', { tab });
    activeTab = tab;
    filter = '';
  }

  /** One event per settled search, not per keystroke. */
  function scheduleSearchTrack() {
    if (searchTimer) clearTimeout(searchTimer);
    const term = filter.trim();
    if (!term) return;
    searchTimer = setTimeout(() => {
      searchTimer = null;
      trackToolUse('tap_drill', 'search', { tab: activeTab, search_term: term });
    }, SEARCH_TRACK_DEBOUNCE_MS);
  }

  onDestroy(() => {
    if (searchTimer) clearTimeout(searchTimer);
  });
</script>

<svelte:head>
  <title>{pageTitle('Tap & Drill Chart')}</title>
</svelte:head>

<div class="tap-drill-page">
  <section class="py-5 bg-light page-hero">
    <div class="container">
      <div class="row mb-4">
        <div class="col-lg-12 text-center">
          <h1 class="section-heading text-uppercase">Tap & Drill Chart</h1>
          <p class="section-subheading text-muted">
            Standard and metric tap drill sizes with clearance drills for close and free fit.
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="py-4">
    <div class="container">
      <div class="row justify-content-center mb-4">
        <div class="col-lg-10">
          <p class="text-muted mb-0">
            <strong>Tap drills:</strong> use 75% thread engagement for aluminum, brass, and
            plastics; 50% for steel, stainless, and iron.
            <strong>Clearance drills:</strong> close fit for precision assemblies; free fit for
            general clearance.
          </p>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-11">
          <div class="card shadow-sm">
            <div class="card-body">
              <ul class="nav nav-pills mb-3" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    type="button"
                    class="nav-link"
                    class:active={activeTab === 'standard'}
                    role="tab"
                    aria-selected={activeTab === 'standard'}
                    on:click={() => setTab('standard')}
                  >
                    Standard (inch)
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    type="button"
                    class="nav-link"
                    class:active={activeTab === 'metric'}
                    role="tab"
                    aria-selected={activeTab === 'metric'}
                    on:click={() => setTab('metric')}
                  >
                    Metric (mm)
                  </button>
                </li>
              </ul>

              <div class="search-row mb-3">
                <label class="form-label visually-hidden" for="tap-filter">Search sizes</label>
                <div class="input-group">
                  <input
                    id="tap-filter"
                    type="search"
                    class="form-control"
                    placeholder={activeTab === 'standard'
                      ? 'Search screw size, TPI, drill…'
                      : 'Search M-size, pitch, drill…'}
                    bind:value={filter}
                    on:input={scheduleSearchTrack}
                    autocomplete="off"
                  />
                  {#if filter}
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      aria-label="Clear search"
                      on:click={() => (filter = '')}
                    >
                      Clear
                    </button>
                  {/if}
                </div>
              </div>

              <TapDrillTable
                variant={activeTab}
                {filter}
                standardRows={STANDARD_TAP_ROWS}
                metricRows={METRIC_TAP_ROWS}
              />
            </div>
          </div>

          <p class="small text-muted mt-3 mb-0">
            Verify critical dimensions before production use.
          </p>
        </div>
      </div>
    </div>
  </section>
</div>

<style>
  .page-hero {
    border-bottom: 1px solid #dee2e6;
  }

  .tap-drill-page .section-subheading {
    margin-bottom: 0;
  }

  .nav-pills .nav-link {
    font-family: Montserrat, sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.03em;
    color: #495057;
  }

  .nav-pills .nav-link.active {
    background-color: #fed136;
    color: #212529;
  }

  @media print {
    .nav-pills,
    .search-row {
      display: none !important;
    }
  }
</style>
